import React, { useEffect, useMemo, useRef, useState } from "react";
import PageIntro from "../../components/PageIntro/PageIntro";
import Table from "../../components/Table/Table";
import Pagination from "../../components/Pagination/Pagination";
import { Button } from "../../components/ui/button";
import { StatusChip } from "../../components/ui-x";
import ChipList from "../../components/RoleLibrary/ChipList";
import {
  useGetLibraryQuery,
  useGetLibraryCountsQuery,
} from "../../reactWrapper/Redux/apiSlice";
import {
  LIBRARY,
  KINDS,
  chain,
  STATUS_CHIP,
} from "../../components/RoleLibrary/library.config";
import ParentSelects from "../../components/RoleLibrary/ParentSelects";
import LibraryFormModal from "../../components/RoleLibrary/LibraryFormModal";
import RowActions from "../../components/RoleLibrary/RowActions";
import BulkUploadModal from "../../components/RoleLibrary/BulkUploadModal";
import BulkDeleteModal from "../../components/RoleLibrary/BulkDeleteModal";

const PAGE_SIZE = 25;

const selectClass =
  "h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

/**
 * Role Library: one tab per level (Categories by default), a cascading
 * parent filter, the table, an Upload button and a per-row menu.
 */
/* "Specialty" -> "specialties", for the "Show all 97 specialties" toggle.
   The library labels are all regular enough for this. */
const plural = (word) => {
  const w = String(word || "item").toLowerCase();
  return w.endsWith("y") ? `${w.slice(0, -1)}ies` : `${w}s`;
};

const RoleLibrary = () => {
  const [tab, setTab] = useState("category");
  const [filter, setFilter] = useState({});
  const [unassigned, setUnassigned] = useState(false);
  const [generic, setGeneric] = useState(false);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // { mode: 'create' } | { mode: 'edit', kind, item }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkMenuRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  /* The menu used to be a <details>, which only ever closes when its own
     summary is clicked again — click anywhere else, or open a modal from
     it, and it stayed hanging over the page. */
  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (!bulkMenuRef.current?.contains(e.target)) setBulkMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setBulkMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [bulkMenuOpen]);

  const levels = chain(tab);
  const deepestParent = [...levels].reverse().find((k) => filter[k]);
  const parentId = deepestParent ? filter[deepestParent] : null;

  /* The API filters by the immediate parent; a filter set higher up the
     chain narrows the dropdown below it, which is what the admin sees. */
  const immediateParent = LIBRARY[tab].parent;
  const apiParentId =
    immediateParent && filter[immediateParent] ? filter[immediateParent] : null;

  const { data: counts } = useGetLibraryCountsQuery();
  const { data, isLoading, isFetching } = useGetLibraryQuery({
    kind: tab,
    parentId: apiParentId || undefined,
    unassigned,
    generic,
    status: status || undefined,
    search: debounced || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const rows = useMemo(() => {
    let list = data?.data || [];
    /* A filter above the immediate parent (e.g. category on the Tools tab)
       is applied here on the chain the API already returns. */
    if (parentId && deepestParent !== immediateParent) {
      list = list.filter((r) => r.chain?.[deepestParent]?.id === parentId);
    }
    return list;
  }, [data, parentId, deepestParent, immediateParent]);

  const switchTab = (k) => {
    setTab(k);
    setFilter({});
    setUnassigned(false);
    setGeneric(false);
    setStatus("");
    setSearch("");
    setPage(1);
  };

  const columns = useMemo(() => {
    const cols = [{ title: "Name", key: "name" }];
    [...levels]
      .reverse()
      .forEach((k) => cols.push({ title: LIBRARY[k].label, key: k }));
    cols.push(
      { title: "Status", key: "status" },
      { title: "", key: "actions" },
    );
    return cols;
  }, [levels]);

  const body = useMemo(
    () =>
      rows.map((r) => {
        const cells = [
          <div key="name" className="flex flex-col">
            <span className="font-semibold text-brand-navy">{r.name}</span>
            {tab === "category" && r.description ? (
              <span className="text-xs text-muted-foreground">
                {r.description}
              </span>
            ) : null}
            {tab === "role" && r.aliases?.length ? (
              <span className="text-xs text-muted-foreground">
                aka {r.aliases.join(", ")}
              </span>
            ) : null}
          </div>,
        ];
        [...levels].reverse().forEach((k) => {
          /* The API returns one row per item now, with every parent on it.
             At the item's own level that is a list — a tool used by four
             specialties, a role in two categories — so it reads as chips
             rather than as four near-identical rows. Levels above stay a
             single name: they are the chain of the first parent. */
          const parents = k === immediateParent ? r.parents || [] : [];
          const name = r.chain?.[k]?.name;
          cells.push(
            parents.length ? (
              /* A popular tool belongs to a hundred specialties; clamped to
                 two rows so one row of the table stays one row. */
              <ChipList
                key={k}
                count={parents.length}
                label={plural(LIBRARY[k]?.label)}>
                {parents.map((p) => (
                  <span
                    key={p.id}
                    className="rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-foreground">
                    {p.name}
                  </span>
                ))}
              </ChipList>
            ) : (
              <span key={k} className="flex flex-wrap gap-1 text-sm">
                {name ? (
                  name
                ) : r.is_generic ? (
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">
                    Generic
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {k === immediateParent ? "Unassigned" : "—"}
                  </span>
                )}
              </span>
            ),
          );
        });
        const chip = STATUS_CHIP[r.status] || STATUS_CHIP[""];
        cells.push(
          <StatusChip key="status" status={chip.status} label={chip.label} />,
        );
        cells.push(
          <div key="actions" className="flex justify-end pr-2">
            <RowActions
              kind={tab}
              item={r}
              onEdit={(item) => setModal({ mode: "edit", kind: tab, item })}
            />
          </div>,
        );
        return cells;
      }),
    [rows, tab, levels, immediateParent],
  );

  const total = data?.pagination?.total ?? 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageIntro
          title="Role Library"
          description="Categories, roles, specialties, tools and skills, and who submitted what."
        />
        <div className="flex items-center gap-2">
          <div className="relative" ref={bulkMenuRef}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={bulkMenuOpen}
              onClick={() => setBulkMenuOpen((o) => !o)}
              className="flex h-10 items-center rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground hover:bg-accent">
              Bulk upload{" "}
              <span className="ml-2 text-xs text-muted-foreground">▼</span>
            </button>
            {bulkMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-border bg-card p-1 shadow-lg">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setBulkMenuOpen(false);
                    setBulkOpen(true);
                  }}
                  className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-accent">
                  Upload to add
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setBulkMenuOpen(false);
                    setBulkDeleteOpen(true);
                  }}
                  className="block w-full rounded px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50">
                  Upload to delete skills
                </button>
              </div>
            ) : null}
          </div>
          <Button
            onClick={() => setModal({ mode: "create" })}
            className="bg-brand-navy text-white hover:bg-brand-navy/90">
            Upload
          </Button>
        </div>
      </div>

      <div
        role="tablist"
        className="flex flex-wrap gap-1 border-b border-border">
        {KINDS.map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => switchTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === k
                ? "border-brand-navy font-semibold text-brand-navy"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            {LIBRARY[k].plural}
            {counts?.[k] ? (
              <span className="ml-1.5 rounded-full bg-surface px-1.5 py-0.5 font-mono text-[11px] font-normal text-muted-foreground">
                {counts[k].total}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {levels.length ? (
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <ParentSelects
              kind={tab}
              value={filter}
              onChange={(next) => {
                setFilter(next);
                setUnassigned(false);
                setGeneric(false);
                setPage(1);
              }}
              mode="filter"
              idPrefix="filter"
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="filter-status"
            className="text-xs font-semibold text-muted-foreground">
            Status
          </label>
          <select
            id="filter-status"
            className={selectClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}>
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="filter-search"
            className="text-xs font-semibold text-muted-foreground">
            Search
          </label>
          <input
            id="filter-search"
            type="search"
            placeholder={`Find a ${LIBRARY[tab].label.toLowerCase()}`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} w-52`}
          />
        </div>
        {immediateParent ? (
          <label className="flex h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={unassigned}
              onChange={(e) => {
                setUnassigned(e.target.checked);
                if (e.target.checked) {
                  setFilter({});
                  setGeneric(false);
                }
                setPage(1);
              }}
            />
            Unassigned only
          </label>
        ) : null}
        {LIBRARY[tab].generic ? (
          <label className="flex h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={generic}
              onChange={(e) => {
                setGeneric(e.target.checked);
                if (e.target.checked) {
                  setFilter({});
                  setUnassigned(false);
                }
                setPage(1);
              }}
            />
            Generic only
          </label>
        ) : null}
        {deepestParent || unassigned || generic || status || search ? (
          <Button
            variant="outline"
            onClick={() => {
              setFilter({});
              setUnassigned(false);
              setGeneric(false);
              setStatus("");
              setSearch("");
              setPage(1);
            }}>
            Clear
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-navy" />
        </div>
      ) : rows.length ? (
        <div className={isFetching ? "opacity-60 transition-opacity" : ""}>
          <Table header={columns} label="" column={body} />
          <section className="w-full py-5">
            <Pagination
              rangeStart={total ? (page - 1) * PAGE_SIZE + 1 : 0}
              totalCount={total}
              currentPage={page}
              pageSize={PAGE_SIZE}
              pageColumn={1}
              siblingCount={1}
              onPageChange={(p) => setPage(p)}
            />
          </section>
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-card py-16">
          <p className="font-medium text-muted-foreground">
            No {LIBRARY[tab].plural.toLowerCase()}{" "}
            {deepestParent || unassigned || generic || status || search
              ? "match this filter"
              : "yet"}
            .
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setModal({ mode: "create" })}>
            Upload a {LIBRARY[tab].label.toLowerCase()}
          </Button>
        </div>
      )}

      <BulkUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
      <BulkDeleteModal
        open={bulkDeleteOpen}
        kind="skill"
        onClose={() => setBulkDeleteOpen(false)}
      />

      <LibraryFormModal
        open={!!modal}
        mode={modal?.mode || "create"}
        kind={modal?.kind}
        item={modal?.item}
        onClose={(result) => {
          setModal(null);
          if (result?.saved && result.kind && result.kind !== tab)
            switchTab(result.kind);
        }}
      />
    </div>
  );
};

export default RoleLibrary;
